import { baseApi } from './baseApi';
import type { JsonObject, JsonValue } from './baseQuery';

const DEFAULT_LIMIT_FOR_BACKEND_FETCHES = 1000;

interface VinResponse extends JsonObject {
    vin: string;
    brand: string;
}

export interface DeviceSharingAccessRules extends JsonObject {
    driverstate_rules: string[];
    assetstate_rules: string[];
}

export interface DeviceSharing extends JsonObject {
    id: string;
    association_id: string;
    shared_association_id: string;
    asset_id: string;
    device_id: string;
    shared_asset_id: string;
    shared_account_id: string;
    shared_account_name?: string | null;
    sharing_started_at?: string | null;
    shared_access_rules?: DeviceSharingAccessRules;
}

interface DeviceSharingsPage {
    deviceSharings: DeviceSharing[];
    nextLink?: string;
}

const isJsonObject = (value: JsonValue | null): value is JsonObject =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const isStringArray = (value: JsonValue | undefined): value is string[] =>
    Array.isArray(value) && value.every(item => typeof item === 'string');

const isVinResponse = (value: JsonValue | null): value is VinResponse =>
    isJsonObject(value) && typeof value.vin === 'string' && typeof value.brand === 'string';

const isDeviceSharing = (value: JsonValue): value is DeviceSharing => {
    if (!isJsonObject(value)) {
        return false;
    }

    const accessRules = value.shared_access_rules;
    const hasValidAccessRules =
        accessRules === undefined ||
        (isJsonObject(accessRules) &&
            isStringArray(accessRules.driverstate_rules) &&
            isStringArray(accessRules.assetstate_rules));

    return (
        typeof value.id === 'string' &&
        typeof value.association_id === 'string' &&
        typeof value.shared_association_id === 'string' &&
        typeof value.asset_id === 'string' &&
        typeof value.device_id === 'string' &&
        typeof value.shared_asset_id === 'string' &&
        typeof value.shared_account_id === 'string' &&
        (value.shared_account_name === undefined ||
            value.shared_account_name === null ||
            typeof value.shared_account_name === 'string') &&
        (value.sharing_started_at === undefined ||
            value.sharing_started_at === null ||
            typeof value.sharing_started_at === 'string') &&
        hasValidAccessRules
    );
};

const toDeviceSharing = (value: JsonValue): DeviceSharing => {
    if (!isDeviceSharing(value)) {
        throw new Error('Invalid device sharing response');
    }

    const accessRules = value.shared_access_rules;
    return {
        id: value.id,
        association_id: value.association_id,
        shared_association_id: value.shared_association_id,
        asset_id: value.asset_id,
        device_id: value.device_id,
        shared_asset_id: value.shared_asset_id,
        shared_account_id: value.shared_account_id,
        shared_account_name: value.shared_account_name,
        sharing_started_at: value.sharing_started_at,
        shared_access_rules: accessRules,
    };
};

const mapDeviceSharingsPage = (rawResult: JsonValue | null): DeviceSharingsPage => {
    if (!isJsonObject(rawResult)) {
        return { deviceSharings: [] };
    }

    const items = rawResult.items;
    const links = rawResult._links;
    const next = isJsonObject(links) ? links.next : undefined;

    return {
        deviceSharings: Array.isArray(items) ? items.filter(isDeviceSharing).map(toDeviceSharing) : [],
        nextLink: isJsonObject(next) && typeof next.href === 'string' ? next.href : undefined,
    };
};

export const assetsApi = baseApi.injectEndpoints({
    endpoints: build => ({
        getBrand: build.query<string | undefined, { vin: string }>({
            query: ({ vin }) => ({ service: 'assetAdmin', path: `/vins/${vin}` }),
            transformResponse: rawResult => (isVinResponse(rawResult) ? rawResult.brand : undefined),
        }),
        getDeviceSharings: build.query<DeviceSharing[], void>({
            async queryFn(_queryArg, _api, _extraOptions, baseQuery) {
                let nextLink: string | undefined;
                const deviceSharings: DeviceSharing[] = [];

                do {
                    const result = await baseQuery(
                        nextLink
                            ? { absoluteUrl: nextLink }
                            : {
                                  service: 'assetAdmin',
                                  path: '/device-sharings',
                                  params: { limit: DEFAULT_LIMIT_FOR_BACKEND_FETCHES },
                              }
                    );

                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    const page = mapDeviceSharingsPage(result.data);
                    deviceSharings.push(...page.deviceSharings);
                    nextLink = page.nextLink;
                } while (nextLink);

                return { data: deviceSharings };
            },
            providesTags: [{ type: 'DeviceSharings', id: 'LIST' }],
        }),
    }),
});

export const { useGetDeviceSharingsQuery, useLazyGetBrandQuery } = assetsApi;
